/*
  La Sura - draws a built cake in the basket (snippets/lasura-cake-thumb.liquid).

  The drawing code is the builder's own (the lasura-cake-draw-*.js files and lasura-cake-draw.js,
  listed in order in data-scripts), loaded here only when a thumb is on the page, so no page
  carries it for nothing. Scripts added by a basket re-render don't run, so each thumb loads what
  it needs itself.

  The product photo inside the element stays until the drawing is ready, and stays for good
  if anything fails. The SVG is decorative: the line's text lists every choice.
*/
const loaded = new Map();

/** @param {string} src */
function load(src) {
  if (!src) return Promise.reject(new Error('No script'));
  if (!loaded.has(src)) {
    loaded.set(
      src,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.append(script);
      })
    );
  }
  return loaded.get(src);
}

class LasuraCakeThumb extends HTMLElement {
  async connectedCallback() {
    let state;
    try {
      state = JSON.parse(this.querySelector('[data-cake-state]')?.textContent || '');
    } catch {
      return;
    }
    try {
      /* In order: each part builds on the ones before. Already there on the builder page. */
      if (!window.LasuraCakeDraw) for (const src of (this.dataset.scripts || '').split(/\s+/).filter(Boolean)) await load(src);
    } catch {
      return;
    }
    if (!this.isConnected || typeof window.LasuraCakeDraw !== 'function') return;

    const markup = window.LasuraCakeDraw(state, {
      slice: false,
      anim: {},
      label: '',
      messageOn: this.dataset.messageOn,
      inside: '',
      pickSponge: '',
      designed: '',
      birthday: '',
      name: '',
    });
    const holder = document.createElement('div');
    holder.innerHTML = markup;
    const svg = holder.querySelector('svg');
    if (!svg) return;
    svg.setAttribute('aria-hidden', 'true');
    svg.removeAttribute('role');
    svg.removeAttribute('aria-label');
    svg.setAttribute('focusable', 'false');

    const image = this.querySelector('img');
    if (image) image.hidden = true;
    this.append(svg);

    /* Crop to the cake itself: the builder's view leaves room for a cut slice and the tallest
       cake, and the stand and its soft shadows reach well past it, so a small cake would sit
       tiny in the thumbnail. The cake group may sit inside a scaled group (a big cake is drawn
       smaller to fit), so its box is taken into the drawing's own units. Measured once it's on
       the page; the stand shows below it. */
    try {
      const cake = svg.querySelector('.lasura-cake-preview__cake') || svg;
      let box = cake.getBBox();
      const m = cake !== svg && svg.getCTM() && cake.getCTM() ? svg.getCTM().inverse().multiply(cake.getCTM()) : null;
      if (m) {
        const xs = [box.x, box.x + box.width].map((x) => [box.y, box.y + box.height].map((y) => new DOMPoint(x, y).matrixTransform(m))).flat();
        const x0 = Math.min(...xs.map((q) => q.x)), y0 = Math.min(...xs.map((q) => q.y));
        box = { x: x0, y: y0, width: Math.max(...xs.map((q) => q.x)) - x0, height: Math.max(...xs.map((q) => q.y)) - y0 };
      }
      if (box.width > 0 && box.height > 0) {
        const side = Math.max(box.width, box.height) * 1.25;
        const x = box.x + box.width / 2 - side / 2;
        const y = box.y + box.height / 2 - side * 0.46;
        svg.setAttribute('viewBox', `${x.toFixed(1)} ${y.toFixed(1)} ${side.toFixed(1)} ${side.toFixed(1)}`);
      }
    } catch {
      svg.setAttribute('viewBox', '0 24 392 330');
    }
  }
}

if (!customElements.get('lasura-cake-thumb')) customElements.define('lasura-cake-thumb', LasuraCakeThumb);
